import { notEmpty, parseString } from "@de-xima/xima-common-js-lang";
import { toJQuery, type TElementSpecifier } from "@de-xima/xima-common-js-dom";
import $ from "jquery";

import { FormTracker } from "../FormTracker.ts";
import type {
  IComponentHandler,
  IComponentHandlerCallbacks,
  IInsertData,
  IInsertionOpts,
  IMaskPanel,
  IPlaceholderMaskOptions,
} from "../Header.ts";

import { NoOpMaskPanel } from "./AbstractMaskPanel.ts";
import { FcHtmlEditorHandler } from "./handler/HtmlEditorHandler.ts";
import { CodeMirrorHandler } from "./handler/CodeMirrorHandler.ts";
import { MonacoEditorHandler } from "./handler/MonacoEditorHandler.ts";
import { SimpleInputHandler } from "./handler/SimpleInputHandler.ts";
import { TagifyHandler } from "./handler/TagifyHandler.ts";
import { DialogDataTableMaskPanel } from "./maskpanel/DialogDataTableMaskPanel.ts";
import { QuillEditorHandler } from "./handler/QuillEditorHandler.ts";

const DATA_PLACEHOLDER_TYPE = "placeholderType";
const DATA_PLACEHOLDER_FOR_ID = "placeholderForId";
const DATA_PLACEHOLDER_FOR_SELECTOR = "placeholderForSelector";
const DATA_PLACEHOLDER_INITIALIZED = "placeholderInit";

const SELECTOR_COMPONENT = ".ui-placeholder-component";
const SELECTOR_COMPONENT_FALLBACK = "[data-placeholder-component='true']";
const SELECTOR_HTML_INPUT = "input";
const SELECTOR_HTML_TEXTAREA = "textarea";
const SELECTOR_P_INPUT_TEXT = ".ui-inputtext";
const SELECTOR_P_INPUT_TEXT_AREA = ".ui-inputtextarea";
const SELECTOR_P_AUTOCOMPLETE = ".ui-autocomplete-input";
const SELECTOR_P_SELECT_ONE_MENU_EDITABLE = "input.ui-selectonemenu-label.ui-inputfield";
const SELECTOR_TRIGGER_BUTTON = ".ui-placeholder-trigger";

/**
 * Keep track of constructed instances.
 */
const IdToInstanceMap = new Map<string, PlaceholderMask>();

/**
 * Input component handlers for different types of components.
 */
let ComponentHandlerRegistry: Map<string, IComponentHandler> | undefined;

function registerDefaultHandlers(registry: Map<string, IComponentHandler>): void {
  // The order here is important -- when attempting to find the correct
  // component type, we iterate over each handler in order and check if it accepts
  // the component.
  // Less specific handlers such as generic INPUT and TEXTAREA handlers must
  // come last. Otherwise more specific components such as a CKEditor would be
  // detected as a TEXTAREA.
  //
  // (Note that the ECMAScript spec guarantees the map iteration in order of insertion.)
  registry.set("inputText", new SimpleInputHandler(SELECTOR_P_INPUT_TEXT));
  registry.set("inputTextArea", new SimpleInputHandler(SELECTOR_P_INPUT_TEXT_AREA));
  registry.set("autocomplete", new SimpleInputHandler(SELECTOR_P_AUTOCOMPLETE));
  registry.set("selectOneMenuEditable", new SimpleInputHandler(SELECTOR_P_SELECT_ONE_MENU_EDITABLE));
  registry.set("tagify", new TagifyHandler());
  registry.set("codeMirror", new CodeMirrorHandler());
  registry.set("monacoEditor", new MonacoEditorHandler());
  registry.set("htmlEditor", new FcHtmlEditorHandler());
  registry.set("qlEditor", new QuillEditorHandler());
  registry.set("htmlInput", new SimpleInputHandler(SELECTOR_HTML_INPUT));
  registry.set("htmlTextArea", new SimpleInputHandler(SELECTOR_HTML_TEXTAREA));
}

function getComponentHandlerRegistry(): Map<string, IComponentHandler> {
  if (ComponentHandlerRegistry === undefined) {
    ComponentHandlerRegistry = new Map();
    registerDefaultHandlers(ComponentHandlerRegistry);
  }
  return ComponentHandlerRegistry;
}

function onPlaceholderRequestFailure(e: unknown): void {
  console.error("Could not request placeholder", e);
}

/**
 * @returns The default placeholder mask options used in the constructor.
 */
function createDefaultOptions(): IPlaceholderMaskOptions {
  return {
    id: "default",
    initTriggerButtons: false,
    maskPanel: new NoOpMaskPanel(),
  };
}

/**
 * Class for managing the interaction between components and the placeholder dialog.
 * It is used as a singleton: One instance is created by the dialog in `placeholder.xhtml`,
 * and subsequently accessed via `Placeholder.default()`.
 */
export class PlaceholderMask {
  /**
   * The mask panel for selecting placeholders.
   */
  private _maskPanel: IMaskPanel;

  /**
   * Options that were passed to the constructor.
   */
  private _options: IPlaceholderMaskOptions;

  /**
   * The currently (most recently) selected input component. By default, the
   * selected placeholder is inserted into this component.
   */
  private _selectedComponent: JQuery;

  /**
   * @param options Options to configure this class.
   */
  constructor(options: Partial<IPlaceholderMaskOptions> = {}) {
    // Evaluate given options and set defaults
    const extendedOptions = Object.assign({}, createDefaultOptions(), options);
    if (extendedOptions.maskPanel === undefined) {
      throw new Error("No mask panel implementation was specified.");
    }

    this._maskPanel = extendedOptions.maskPanel;

    this._options = extendedOptions;

    this._selectedComponent = $();

    // If enabled, add listener for placeholder buttons.
    if (extendedOptions.initTriggerButtons) {
      $(document).on("click", SELECTOR_TRIGGER_BUTTON, (event) => {
        if (event.currentTarget instanceof HTMLElement) {
          this.requestForButton(event.currentTarget);
        }
      });
    }

    this._maskPanel.setInsertionListener((opts) => this._onInsertion(opts));
  }

  /**
   * @returns ID of this placeholder mask.
   */
  getId(): string {
    return this._options.id;
  }

  /**
   * Returns the placeholder selection panel. You may need the panel if you want to open or close it.
   * @returns Thw mask panel active for this mask.
   */
  getPanel(): IMaskPanel {
    return this._maskPanel;
  }

  /**
   * Inserts the given placeholder to the given component. You may specify
   * optional settings for inserting. Defaults to inserting the currently
   * selected placeholder into the current caret position of the input
   * component, not closing the placeholder mask.
   * @param opts for inserting the text.
   */
  async insert(opts: Partial<IInsertionOpts> = {}): Promise<void> {
    // Resolve defaults
    const $component = toJQuery(opts.component ?? this._findSelectedComponent());
    const toInsert = opts.placeholder ??
      (await this._maskPanel.getSelectedValue()) ?? { insertHint: "text", content: "" };

    // If no value was given and no selected or active was found, stop.
    if (toInsert.content.length === 0) {
      console.warn(
        "Cannot insert placeholder into components, no placeholder value was given and no currently selected was found.",
      );
      return;
    }

    if ($component.length === 0) {
      console.warn("Cannot insert placeholder into components, no component given and no currently active was found.");
      return;
    }

    this._insert($component, toInsert, opts.close ?? false);
  }

  /**
   * Called when any change (such as loading components dynamically) was performed that
   * may necessitate updating our own state. This method may be called after the page was
   * modified via AJAX request (JSF update).
   */
  onExternalChange(): void {
    this._scanForComponents();
    this._maskPanel.onExternalChange();
  }

  /**
   * Open the placeholder mask for the given button. The input component is determined as follows:
   * - if the button has a `data-placeholder-for-id` attribute set, the element with that ID
   * - otherwise, if the button has a `data-placeholder-for-selector` attribute set, the element for that selector
   * - otherwise, if any parent has the class ui-placeholder-component, that parent
   * If no input component could be determined, log an error and do nothing.
   * @param button Button for which to open the placeholder mask.
   */
  requestForButton(button: TElementSpecifier<HTMLElement>): void {
    const $button = toJQuery(button);
    let $element = $();
    // Try the given ID
    if ($element.length === 0) {
      const id = parseString($button.data(DATA_PLACEHOLDER_FOR_ID));
      if (id !== "") {
        $element = toJQuery(document.getElementById(id));
      }
    }

    // Try the given selector
    if ($element.length === 0) {
      const selector = parseString($button.data(DATA_PLACEHOLDER_FOR_SELECTOR));
      if (selector !== "") {
        $element = $(selector);
      }
    }

    // Try a parent element, if this button is a child of an input component
    if ($element.length === 0) {
      $element = $button.closest(SELECTOR_COMPONENT);
    }
    if ($element.length === 0) {
      $element = $button.closest(SELECTOR_COMPONENT_FALLBACK);
    }

    // No input component found
    if ($element.length !== 1) {
      console.warn("No input component found for placeholder trigger button", button);
      return;
    }

    this._onPlaceholderRequested($element).catch(onPlaceholderRequestFailure);
  }

  /**
   * @param $element Component for which to determine the handler.
   * @returns The handler for the given input component.
   */
  private _findComponentHandler($element: JQuery): IComponentHandler | undefined {
    const type = this._findComponentType($element);
    if (type === undefined) {
      console.warn("Cannot determine type for component", $element);
      return undefined;
    }
    return getComponentHandlerRegistry().get(type);
  }

  /**
   * @param $element An element with the SELECTOR_COMPONENT class.
   * @returns The type of the given input component.
   */
  private _findComponentType($element: JQuery): string | undefined {
    const type = parseString($element.data(DATA_PLACEHOLDER_TYPE));
    if (notEmpty(type)) {
      return type;
    }
    for (const [key, handler] of getComponentHandlerRegistry()) {
      if (handler.accepts($element)) {
        // Save the type for future access.
        $element.data(DATA_PLACEHOLDER_TYPE, key);
        return key;
      }
    }
    return undefined;
  }

  /**
   * @returns The currently selected or active input component, into which placeholders are to be inserted.
   */
  private _findSelectedComponent(): JQuery {
    return this._selectedComponent;
  }

  /**
   * Inserts text into an input field.
   * @param data Value to be inserted.
   * @param $element Input component into which to insert.
   * @param close Whether to close the input mask after insertion.
   */
  private _insert($element: JQuery, data: IInsertData, close: boolean): void {
    const handler = this._findComponentHandler($element);
    if (handler === undefined) {
      console.warn("Cannot find handler for component", $element);
      return;
    }
    handler.insertText($element, data);

    // Inform the form tracker that the form data may have changed.
    // The save button may need to be highlighted etc.
    FormTracker.getInstance().changed($element);

    if (close) {
      this._maskPanel.close();
      handler.focus($element);
    }
  }

  /**
   * @param $element Element for the input component.
   * @returns Whether the input component was initialized.
   * @private
   */
  private _isInitialized($element: JQuery): boolean {
    return $element.data(DATA_PLACEHOLDER_INITIALIZED) === "true";
  }

  /**
   * Called when the user focuses an input component.
   * @param $element Input component which was focused.
   */
  private _onFocus($element: JQuery) {
    this._selectedComponent = $element;
    this._maskPanel.focused();
  }

  /**
   * Callback when text needs to be inserted.
   * @param opts Options for performing the insertion.
   */
  private _onInsertion(opts: IInsertionOpts) {
    // Resolve defaults
    const $component = toJQuery(this._findSelectedComponent());
    this._insert($component, opts.placeholder, opts.close ?? false);
  }

  /**
   * Called when the user is focused on an input component and requests a placeholder.
   * @param $element Input component for which a placeholder was requested.
   */
  private async _onPlaceholderRequested($element: JQuery): Promise<void> {
    const handler = this._findComponentHandler($element);
    if (handler !== undefined) {
      const $inputElement = (await this._maskPanel.isOpen()) ? undefined : handler.getInputElement($element);
      this._selectedComponent = $element;
      this._maskPanel.open($inputElement ?? $());
    }
  }

  /**
   * Scans the given context for all input components requiring a placeholder.
   * Initialize all components that were found. Components already initialized
   * are not initialized again.
   * @param context Context to which to limit the
   * search. Default to the entire document.
   */
  private _scanForComponents(context?: TElementSpecifier<HTMLElement>): void {
    const $context = context ? toJQuery(context) : $(document);
    for (const element of $context.find(SELECTOR_COMPONENT)) {
      const $element = $(element);
      const handler = this._findComponentHandler($element);
      if (handler === undefined) {
        console.warn("Cannot find handler for component", element);
        return;
      }
      if (!this._isInitialized($element)) {
        const callbacks: IComponentHandlerCallbacks = {
          onFocus: () => this._onFocus($element),
          onPlaceholderRequested: () => {
            this._onPlaceholderRequested($element).catch(onPlaceholderRequestFailure);
          },
        };
        if (handler.initialize($element, callbacks)) {
          this._setInitialized($element, true);
        } else {
          // Can happen e.g. if PrimeFaces component widget JavaScript was not yet loaded.
          const MaxPollTime = 60000;
          const PollInterval = 250;
          const start = Date.now();
          const id = setInterval(() => {
            if (handler.initialize($element, callbacks)) {
              this._setInitialized($element, true);
              clearInterval(id);
              return;
            }
            if (Date.now() - start > MaxPollTime) {
              console.warn(`Could not initialize placeholder component after ${MaxPollTime}ms`, element);
              clearInterval(id);
              return;
            }
          }, PollInterval);
        }
      }
    }
  }

  /**
   * @param $element Element for the input component.
   * @param initialized Whether the component was initialized.
   * @private
   */
  private _setInitialized($element: JQuery, initialized: boolean): void {
    $element.data(DATA_PLACEHOLDER_INITIALIZED, String(initialized));
  }

  /**
   * Create a new instance of the placeholder mask.
   * @param options Options for the placeholder mask.
   * @returns The newly created instance.
   */
  static create(options: IPlaceholderMaskOptions): PlaceholderMask {
    const instance = new PlaceholderMask(options);
    const id = instance.getId();
    const existingInstance = PlaceholderMask.forId(id);
    if (existingInstance !== undefined) {
      console.warn("Placeholder mask for id", id, "was already created.");
    }
    IdToInstanceMap.set(id, instance);
    return instance;
  }

  /**
   * @returns The default placeholder mask instance. `undefined` if it was not
   * created yet.
   */
  static default(): PlaceholderMask | undefined {
    return PlaceholderMask.forId("default");
  }

  /**
   * @param id ID of the placeholder mask to create.
   * @returns The placeholder mask instance for the given id. `undefined` if it
   * was not created yet.
   */
  static forId(id: string): PlaceholderMask | undefined {
    return IdToInstanceMap.get(id);
  }
}

export function initDefaultPlaceholderMask(): void {
  PlaceholderMask.create({
    id: "default",
    initTriggerButtons: true,
    maskPanel: DialogDataTableMaskPanel.default(),
  });
}
